// src/components/admin/SuperAdminUserCard.js - User card for SuperAdmin tenant user management
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';
import '../../styles/globals.css';
import './SuperAdminUserCard.css';

const SuperAdminUserCard = ({
  user,
  tenant,
  currentUserId,
  onSuspend,
  onReactivate,
  onMigrate,
  onDelete,
  onUpdateRoles,
  onRegenerateLink,
  availableRoles,
  isLoading,
  getStatusBadgeClass,
  formatDate
}) => {
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [migrateError, setMigrateError] = useState(null);
  const [roleError, setRoleError] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [migrateLoading, setMigrateLoading] = useState(false);
  const [roleLoading, setRoleLoading] = useState(false);

  const isCurrentUser = user.id === currentUserId;
  const isSuperAdmin = user.roles && user.roles.includes('SuperAdmin');
  const isSuspended = user.status === 'suspended';
  const isInvited = user.status === 'invited';

  // Don't allow operations on SuperAdmin users or current user
  const canModify = !isSuperAdmin && !isCurrentUser;

  const handleSuspendToggle = async () => {
    if (!canModify) return;

    try {
      if (isSuspended) {
        await onReactivate(user.id);
      } else {
        await onSuspend(user.id);
      }
    } catch (err) {
      console.error('Suspend/Reactivate failed:', err);
      // Error will be handled by parent component's useApiCall hook
    }
  };

  const handleMigrate = async (targetTenantId, reason) => {
    if (!canModify) return;
    
    try {
      setMigrateError(null); // Clear any previous errors
      setMigrateLoading(true);
      await onMigrate(user.id, targetTenantId, reason);
      setShowMigrateModal(false); // Only close on success
    } catch (err) {
      console.error('Migrate failed:', err);
      setMigrateError(err.message || 'Failed to migrate user. Please try again.');
      // Don't close the modal - let user see the error and try again
    } finally {
      setMigrateLoading(false);
    }
  };

  const handleUpdateRoles = async (roles) => {
    if (!canModify) return;

    try {
      setRoleError(null); // Clear any previous errors
      setRoleLoading(true);
      await onUpdateRoles(user.id, roles);
      setShowRoleModal(false); // Only close on success
    } catch (err) {
      console.error('Update roles failed:', err);
      setRoleError(err.message || 'Failed to update roles. Please try again.');
      // Don't close the modal - let user see the error and try again
    } finally {
      setRoleLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!canModify) return;
    
    try {
      setDeleteError(null); // Clear any previous errors
      setDeleteLoading(true);
      await onDelete(user.id);
      setShowDeleteModal(false); // Only close on success
    } catch (err) {
      // Catch the error and display it in the modal
      console.error('Delete failed:', err);
      setDeleteError(err.message || 'Failed to delete user. Please try again.');
      // Don't close the modal - let user see the error and try again
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <div className="card">
        <div className="flex flex-between gap-4">
          {/* User Info Section */}
          <div className="flex-1">
            <div className="flex gap-3 mb-2" style={{ alignItems: 'center' }}>
              <h3 className="text-lg font-semibold text-gray-800">
                {user.displayName || user.email}
              </h3>
              <span className={getStatusBadgeClass(user.status)}>
                {user.status}
              </span>
            </div>

            {user.displayName && (
              <p className="text-sm text-gray-600 mb-1">{user.email}</p>
            )}

            {/* Tenant Context - Important for SuperAdmin */}
            <div className="flex gap-2 mb-2" style={{ alignItems: 'center' }}>
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span className="text-sm font-medium text-gray-700">{tenant.name}</span>
              <span className="text-sm text-gray-500">({tenant.slug})</span>
            </div>

            <div className="flex gap-4 mb-2">
              <div className="text-sm text-gray-600">
                <strong>Roles:</strong> {user.roles && user.roles.length > 0 ? user.roles.join(', ') : 'None'}
              </div>
            </div>

            <div className="flex gap-4 text-sm text-gray-500">
              {user.lastLogin ? (
                <span>Last login: {formatDate(user.lastLogin)}</span>
              ) : user.invitedAt ? (
                <span>Invited: {formatDate(user.invitedAt)}</span>
              ) : (
                <span>No login recorded</span>
              )}
            </div>

            {/* Warning messages */}
            {isSuperAdmin && (
              <div className="mt-2 text-sm text-purple-600 font-medium">
                ⚠ SuperAdmin - Cannot be modified
              </div>
            )}
            {isCurrentUser && (
              <div className="mt-2 text-sm text-orange-600 font-medium">
                ⚠ This is your account - Cannot be modified
              </div>
            )}
          </div>

          {/* Actions Section */}
          <div className="flex flex-col gap-2" style={{ minWidth: '140px' }}>
            {canModify ? (
              <>
                {/* Suspend/Reactivate Button, or a new invite link for an invited user */}
                {isInvited ? (
                  <button
                    onClick={() => onRegenerateLink(user)}
                    disabled={isLoading}
                    className="btn btn-secondary btn-sm"
                    title="Create a new invite link to copy and share; the current link stops working"
                  >
                    Regenerate link
                  </button>
                ) : (
                  <button
                    onClick={handleSuspendToggle}
                    disabled={isLoading}
                    className={`btn btn-sm ${isSuspended ? 'btn-primary' : 'btn-warning'}`}
                  >
                    {isSuspended ? 'Reactivate' : 'Suspend'}
                  </button>
                )}

                {/* Change Role Button */}
                <button
                  onClick={() => setShowRoleModal(true)}
                  disabled={isLoading}
                  className="btn btn-secondary btn-sm"
                >
                  Change Role
                </button>

                {/* Migrate Button */}
                <button
                  onClick={() => setShowMigrateModal(true)}
                  disabled={isLoading}
                  className="btn btn-secondary btn-sm"
                >
                  Migrate
                </button>

                {/* Delete Button */}
                <button
                  onClick={() => setShowDeleteModal(true)}
                  disabled={isLoading}
                  className="btn btn-danger btn-sm"
                >
                  Delete
                </button>
              </>
            ) : (
              <div className="text-sm text-gray-400 text-center py-2">
                No actions available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Change Role Modal */}
      {showRoleModal && (
        <ChangeRoleModal
          user={user}
          availableRoles={availableRoles}
          onClose={() => {
            setShowRoleModal(false);
            setRoleError(null); // Clear error when closing
          }}
          onSubmit={handleUpdateRoles}
          loading={roleLoading}
          error={roleError}
        />
      )}

      {/* Migrate User Modal */}
      {showMigrateModal && (
        <MigrateUserModal
          user={user}
          currentTenant={tenant}
          onClose={() => {
            setShowMigrateModal(false);
            setMigrateError(null); // Clear error when closing
          }}
          onSubmit={handleMigrate}
          loading={migrateLoading}
          error={migrateError}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <ConfirmDeleteModal
          user={user}
          tenant={tenant}
          onClose={() => {
            setShowDeleteModal(false);
            setDeleteError(null); // Clear error when closing
          }}
          onConfirm={handleDelete}
          loading={deleteLoading}
          error={deleteError}
        />
      )}
    </>
  );
};

// Change Role Modal Component
const ChangeRoleModal = ({ user, availableRoles, onClose, onSubmit, loading, error }) => {
  const [roles, setRoles] = useState(user.roles || []);
  const [validationError, setValidationError] = useState('');

  const handleRoleChange = (roleId, checked) => {
    setRoles(prev => checked ? [...prev, roleId] : prev.filter(r => r !== roleId));
    if (validationError) setValidationError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (roles.length === 0) {
      setValidationError('At least one role must be selected');
      return;
    }

    onSubmit(roles);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3 className="modal-title">Change Role: {user.displayName || user.email}</h3>
          <button onClick={onClose} className="modal-close" disabled={loading}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border-2 border-red-400 rounded-md">
              <div className="flex gap-2">
                <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-bold text-red-800 mb-1">Update Failed</p>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Validation Error */}
          {validationError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{validationError}</p>
            </div>
          )}

          <div className="mb-6">
            <label className="form-label">Roles</label>
            <div className="checkbox-group">
              {availableRoles.map(role => (
                <div key={role.id || role.name} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={roles.includes(role.id)}
                    onChange={(e) => handleRoleChange(role.id, e.target.checked)}
                    disabled={loading}
                  />
                  <div className="checkbox-label">
                    <strong>{role.name}</strong>
                    {role.description && <div className="checkbox-description">{role.description}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Roles'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Migrate User Modal Component
const MigrateUserModal = ({ user, currentTenant, onClose, onSubmit, loading, error }) => {
  const { user: authUser } = useAuth();
  const [targetTenantId, setTargetTenantId] = useState('');
  const [reason, setReason] = useState('');
  const [availableTenants, setAvailableTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Load available tenants on mount
  React.useEffect(() => {
    const loadTenants = async () => {
      try {
        setLoadingTenants(true);
        setLoadError(null);
        
        // Fetch all tenants from API using apiService
        const data = await apiService.get('/api/v1/tenants?page=1&limit=100&status=active', authUser);
        
        if (data.success && data.data?.tenants) {
          // Filter out the current tenant
          const otherTenants = data.data.tenants.filter(t => t.id !== currentTenant.id);
          setAvailableTenants(otherTenants);
        } else {
          throw new Error('Invalid response format');
        }
        
        setLoadingTenants(false);
      } catch (err) {
        console.error('Error loading tenants:', err);
        setLoadError('Failed to load tenant list. ' + (err.message || 'Please try again.'));
        setLoadingTenants(false);
      }
    };

    loadTenants();
  }, [authUser, currentTenant.id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!targetTenantId) return;
    
    onSubmit(parseInt(targetTenantId), reason.trim() || undefined);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3 className="modal-title">Migrate User to Different Tenant</h3>
          <button onClick={onClose} className="modal-close" disabled={loading}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Migration Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border-2 border-red-400 rounded-md">
              <div className="flex gap-2">
                <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-bold text-red-800 mb-1">Migration Failed</p>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* User Info */}
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
            <p className="text-sm font-medium text-gray-700 mb-1">Migrating User:</p>
            <p className="text-sm text-gray-600">User: <strong>{user.email}</strong></p>
            <p className="text-sm text-gray-600">Name: <strong>{user.displayName || 'N/A'}</strong></p>
            <p className="text-sm text-gray-600">Current Tenant: <strong>{currentTenant.name}</strong></p>
          </div>

          {/* Target Tenant Selection */}
          <div className="mb-4">
            <label className="form-label">
              Destination Tenant *
            </label>
            {loadingTenants ? (
              <div className="text-sm text-gray-500 py-2">Loading tenants...</div>
            ) : loadError ? (
              <div className="text-sm text-red-600 py-2">{loadError}</div>
            ) : availableTenants.length === 0 ? (
              <div className="text-sm text-gray-500 py-2">No other active tenants available</div>
            ) : (
              <select
                value={targetTenantId}
                onChange={(e) => setTargetTenantId(e.target.value)}
                className="form-input"
                disabled={loading}
                required
              >
                <option value="">Select a tenant...</option>
                {availableTenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name} ({tenant.slug})
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Reason (Optional) */}
          <div className="mb-6">
            <label className="form-label">
              Reason for Migration (Optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="form-input"
              rows="3"
              placeholder="e.g., User changed departments, moved to different business unit..."
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              This will be logged in the audit trail
            </p>
          </div>

          <div className="modal-footer">
            <button 
              type="button" 
              onClick={onClose} 
              className="btn btn-secondary flex-1" 
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-primary flex-1" 
              disabled={loading || !targetTenantId || loadingTenants || loadError}
            >
              {loading ? 'Migrating...' : 'Migrate User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Confirm Delete Modal Component
const ConfirmDeleteModal = ({ user, tenant, onClose, onConfirm, loading, error }) => {
  const [understood, setUnderstood] = useState(false);

  const canDelete = understood;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (canDelete) {
      onConfirm();
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3 className="modal-title text-red-600">Delete User - Permanent Action</h3>
          <button onClick={onClose} className="modal-close" disabled={loading}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Error Message */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border-2 border-red-400 rounded-md">
              <div className="flex gap-2">
                <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-bold text-red-800 mb-1">Delete Failed</p>
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Danger Banner */}
          <div className="mb-4 p-4 bg-red-50 border-2 border-red-300 rounded-md">
            <div className="flex gap-2">
              <svg className="w-6 h-6 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-sm font-bold text-red-800 mb-2">
                  ⚠ WARNING: This action cannot be undone!
                </p>
                <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                  <li>The user will be permanently deleted</li>
                  <li>All conversations and messages will be deleted</li>
                  <li>All artifacts and files will be deleted</li>
                  <li>This data cannot be recovered</li>
                </ul>
              </div>
            </div>
          </div>

          {/* User Info */}
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
            <p className="text-sm font-medium text-gray-700 mb-1">You are about to delete:</p>
            <p className="text-sm text-gray-600">User: <strong>{user.email}</strong></p>
            <p className="text-sm text-gray-600">Name: <strong>{user.displayName || 'N/A'}</strong></p>
            <p className="text-sm text-gray-600">Tenant: <strong>{tenant.name}</strong></p>
          </div>

          {/* Confirmation Checkbox */}
          <div className="mb-6">
            <label className="flex gap-2 items-start cursor-pointer">
              <input
                type="checkbox"
                checked={understood}
                onChange={(e) => setUnderstood(e.target.checked)}
                disabled={loading}
                className="mt-1"
              />
              <span className="text-sm text-gray-700">
                I understand that this action is permanent and cannot be undone. All user data will be deleted in compliance with GDPR requirements.
              </span>
            </label>
          </div>

          <div className="modal-footer">
            <button 
              type="button" 
              onClick={onClose} 
              className="btn btn-secondary flex-1" 
              disabled={loading}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="btn btn-danger flex-1" 
              disabled={loading || !canDelete}
            >
              {loading ? 'Deleting...' : 'Delete User Permanently'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// PropTypes
SuperAdminUserCard.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    email: PropTypes.string.isRequired,
    displayName: PropTypes.string,
    status: PropTypes.string.isRequired,
    roles: PropTypes.arrayOf(PropTypes.string),
    lastLogin: PropTypes.string,
    invitedAt: PropTypes.string
  }).isRequired,
  tenant: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired
  }).isRequired,
  currentUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSuspend: PropTypes.func.isRequired,
  onReactivate: PropTypes.func.isRequired,
  onMigrate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onUpdateRoles: PropTypes.func.isRequired,
  onRegenerateLink: PropTypes.func.isRequired,
  availableRoles: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string.isRequired,
    description: PropTypes.string
  })).isRequired,
  isLoading: PropTypes.bool.isRequired,
  getStatusBadgeClass: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired
};

ChangeRoleModal.propTypes = {
  user: PropTypes.object.isRequired,
  availableRoles: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string
};

MigrateUserModal.propTypes = {
  user: PropTypes.object.isRequired,
  currentTenant: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string
};

ConfirmDeleteModal.propTypes = {
  user: PropTypes.object.isRequired,
  tenant: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string
};

export default SuperAdminUserCard;